import React, { FC, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'
import { NumberParam, StringParam, useQueryParams } from 'use-query-params'
import { Button, ModalVariables, SearchComponent, Select, StartPage, Tabs } from '../../../components'
import preorderHardwareService, {
  PreorderHardware,
  PreorderHardwareList,
  PreorderHardwareType,
} from '../../../api/preorderHardwareService'
import { useUi } from '../../../context/ui.context'
import { usePageTitle } from '../../../context/navigationContext'
import { extractErrorMessage } from '../../../utils/error-extractor'
import { icons } from '../../../icons'
import {
  Filters, FilterSelect, SearchField, SearchRow, Status, StatusDot,
} from '../styles'
import { hardwareConfig, hardwarePath, hardwareTypes, isHardwareType } from './config'
import { HardwareListPage } from './styles'

const PreorderHardwarePage: FC = () => {
  const [t] = useTranslation()
  const { viewAlert } = useUi()
  const navigate = useNavigate()
  const location = useLocation()
  const [query, setQuery] = useQueryParams({
    tab: StringParam, page: NumberParam, page_size: NumberParam,
    search: StringParam, ordering: StringParam, is_active: StringParam,
  })
  const type = isHardwareType(query.tab) ? query.tab : 'cpus'
  const page = Math.max(1, query.page || 1)
  const pageSize = Math.max(1, query.page_size || 10)
  const { search, ordering, is_active: isActive } = query
  const previousSearch = useRef(search)
  const [dataByType, setDataByType] = useState<Partial<Record<PreorderHardwareType, PreorderHardwareList>>>({})
  const loadedTypes = useRef(new Set<PreorderHardwareType>())
  const data = dataByType[type]
  const [loading, setLoading] = useState(true)
  const [errorMessage, setErrorMessage] = useState('')
  const [reload, setReload] = useState(0)
  const [deleting, setDeleting] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<{
    type: PreorderHardwareType;
    item: PreorderHardware;
  } | null>(null)

  usePageTitle(t('PREORDER_HARDWARE'))

  useEffect(() => {
    if (previousSearch.current !== search) {
      previousSearch.current = search
      if (page !== 1) {
        setQuery({ page: 1 }, 'replaceIn')
        return
      }
    }
    const controller = new AbortController()
    let inFlight = false
    setErrorMessage('')
    const load = async () => {
      if (inFlight || controller.signal.aborted) return
      inFlight = true
      try {
        const { data: result } = await preorderHardwareService.list(type, {
          page, page_size: pageSize,
          search: search || undefined,
          ordering: ordering || undefined,
          is_active: isActive === 'true' ? true : isActive === 'false' ? false : undefined,
        }, controller.signal)
        if (controller.signal.aborted) return
        const lastPage = Math.max(1, Math.ceil(result.count / pageSize))
        if (page > lastPage) {
          setQuery({ page: lastPage }, 'replaceIn')
          return
        }
        loadedTypes.current.add(type)
        setErrorMessage('')
        setDataByType((previous) => {
          const current = previous[type]
          if (current?.count === result.count
            && current.next === result.next
            && current.previous === result.previous
            && JSON.stringify(current.results) === JSON.stringify(result.results)) return previous
          return { ...previous, [type]: result }
        })
      } catch (error: any) {
        if (!controller.signal.aborted && !loadedTypes.current.has(type)) {
          setErrorMessage(extractErrorMessage(error?.response?.data) || t('PREORDER_LOAD_FAILED'))
        }
      } finally {
        inFlight = false
        if (!controller.signal.aborted) setLoading(false)
      }
    }
    load()
    const interval = window.setInterval(load, 10000)
    return () => {
      window.clearInterval(interval)
      controller.abort()
    }
  }, [type, page, pageSize, search, ordering, isActive, reload, setQuery, t])

  const openForm = (id?: string) => navigate(
    `${hardwarePath}/${type}/${id ? `${id}/edit` : 'create'}`,
    { state: { returnSearch: location.search } },
  )

  const confirmDelete = async () => {
    if (!deleteTarget || deleting) return
    setDeleting(true)
    try {
      await preorderHardwareService.delete(deleteTarget.type, deleteTarget.item.id)
      setDeleteTarget(null)
      viewAlert({ severity: 'success', message: t('PREORDER_DELETED') })
      setReload((value) => value + 1)
    } catch (error: any) {
      viewAlert({ severity: 'error', message: extractErrorMessage(error?.response?.data) || t('ERROR_OCCURRED') })
    } finally {
      setDeleting(false)
    }
  }

  const columns = [
    { title: t('NAME'), key: 'name', sorted: true },
    { title: t('PREORDER_MODEL'), key: 'model', sorted: true },
    ...(type === 'disks' ? [{ title: t('DEDICATED_DISK_TYPE'), key: 'disk_type', sorted: true }] : []),
    ...hardwareConfig[type].fields.map((field) => ({ title: t(field.label), key: field.key, sorted: true })),
    { title: t('STATUS'), key: 'status' },
    { title: '', key: 'props' },
  ]
  const rows = (data?.results || []).map((item) => ({
    ...item,
    status: (
      <Status status={item.is_active ? 'active' : 'disabled'}>
        <StatusDot status={item.is_active ? 'active' : 'disabled'} />
        {t(item.is_active ? 'ACTIVE' : 'INACTIVE')}
      </Status>
    ),
  }))

  return (
    <HardwareListPage>
      <StartPage
        title={t('PREORDER_HARDWARE')}
        noResourceText={errorMessage || String(t(data ? 'PREORDER_NO_HARDWARE' : 'LOADING'))}
        haveResource={rows.length > 0}
        pageLoading={loading}
        withoutCreateResourceButton
        rightCustomCreateButton={(
          <Filters>
            <Tabs
              size="small"
              customStyles={{ gap: '28px' }}
              blockNavigate
              location={type}
              tabs={hardwareTypes.map((value) => ({ id: value, slug: value, title: t(hardwareConfig[value].label) }))}
              onChange={(tab: PreorderHardwareType) => setQuery({ tab, page: 1, ordering: undefined })}
            />
            <SearchRow>
              <Button title={t(hardwareConfig[type].create)} variant="primary" size="display" onClick={() => openForm()} />
              <SearchField><SearchComponent /></SearchField>
              <FilterSelect>
                <Select
                  title={t('STATUS')}
                  isSmall
                  selectedValue={isActive || ''}
                  data={[
                    { value: '', label: t('ALL') },
                    { value: 'true', label: t('ACTIVE') },
                    { value: 'false', label: t('INACTIVE') },
                  ]}
                  onChange={(value) => setQuery({ is_active: value || undefined, page: 1 })}
                />
              </FilterSelect>
            </SearchRow>
          </Filters>
        )}
        tableData={{
          columns,
          data: rows,
          paginationData: data,
          hideCheckbox: true,
          showSearchSkeleton: false,
          props: [
            { id: 'edit', title: t('EDIT'), icon: icons.Edit, onClick: (item: PreorderHardware) => openForm(item.id) },
            { id: 'delete', title: t('DELETE'), icon: icons.DeleteIcon, onClick: (item: PreorderHardware) => setDeleteTarget({ type, item }) },
          ],
        }}
      />
      <ModalVariables
        viewModal={Boolean(deleteTarget)}
        activeAnimation={Boolean(deleteTarget)}
        modalType="delete"
        alertTitle={t(hardwareConfig[deleteTarget?.type || type].label)}
        alertSubTitle={deleteTarget ? [deleteTarget.item] : []}
        loading={deleting}
        closeModal={confirmDelete}
        closeModalCancel={() => { if (!deleting) setDeleteTarget(null) }}
      />
    </HardwareListPage>
  )
}

export default PreorderHardwarePage
